import React from "react";
import type { ApolloClient } from "@apollo/client";
import { useLocation } from "react-router-dom";
import { connect, type ConnectedProps } from "react-redux";
import { withApollo, type WithApolloClient } from "@apollo/client/react/hoc";
import { groupBy } from "lodash";
import { push } from "store/actions/router";

import Employees from "./Employees";
import UploadEmployees from "./UploadEmployees";
import UploadSingle from "pages/dashboards/employerDashboard/userManagement/UploadSingle";

import {
  getAllEmployerGroups,
  getEmployeeInvitations,
} from "actions/employerActions";
import type { RootState } from "types/state";

interface OwnProps {
  // Injected by the UserManagement_WithLocation wrapper below, not by connect.
  routeName?: string;
  // Declared on the original propTypes spec but never supplied by the wrapper
  // or connect, and never read in the class body. Kept optional.
  navTab?: {
    tabObject?: string;
    tabValue?: string;
  };
}

const mapStateToProps = (state: RootState) => {
  let groupWithEmployees = groupBy(
    state.employer.linkedEmployees,
    "groupWithSchedule.group.id"
  );
  return { groups: state.employer.groups, groupWithEmployees };
};

const mapDispatchToProps = {
  push,
  getAllEmployerGroups,
  getEmployeeInvitations,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = WithApolloClient<OwnProps & PropsFromRedux>;

interface State {
  tab: string;
  activeStep: number;
}

class UserManagement extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      tab: this._calcActiveTab(props.routeName),
      activeStep: 1,
    };
  }

  componentDidMount() {
    // hydration calls for data needed by sub components
    void this.props.getAllEmployerGroups(
      this.props.client as ApolloClient<object>
    );
    void this.props.getEmployeeInvitations(
      this.props.client as ApolloClient<object>
    );
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.routeName !== this.props.routeName) {
      this.setState({ tab: this._calcActiveTab(this.props.routeName) });
    }
  }

  _calcActiveTab(routeName: string | undefined) {
    switch (routeName) {
      case "/dashboard/users/employees":
        return "employees";
      case "/dashboard/users/employees/upload":
        return "employees/upload";
      case "/dashboard/users/employees/upload-single":
        return "employees/upload-single";
      default:
        return "employees";
    }
  }

  _employeesContent = () => {
    return (
      <section className="page-title-wrap">
        <article className="text-cell">
          <div className="dashboard-header">
            <h1 className="page-title">User Management</h1>
            <p className="page-subtext">
              Invite employees or manage existing users.
            </p>
          </div>
          <div>
            <Employees />
          </div>
        </article>
      </section>
    );
  };

  _rosterContent = () => {
    return <UploadEmployees />;
  };

  _getContent = () => {
    switch (this.state.tab) {
      case "employees":
        return <Employees />;
      case "employees/upload":
        return <UploadEmployees />;
      case "employees/upload-single":
        return <UploadSingle />;
    }
  };

  render() {
    const content = this._getContent();

    return <div className="mega-container">{content}</div>;
  }
}

const UserManagement_WithLocation = (props: Props) => {
  const { pathname: routeName } = useLocation();
  return <UserManagement {...props} routeName={routeName} />;
};

export default connector(withApollo<Props>(UserManagement_WithLocation));
